import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { 
  FaSearch, 
  FaTimes, 
  FaLayerGroup, 
  FaGraduationCap, 
  FaPlusCircle, 
  FaCheckCircle 
} from "react-icons/fa";
import Fuse from "fuse.js";
import { RenderMarkdownWithLatex } from "../../utils/utils";
// Dodajemy import API
import api from "../../api/apiHelper";

const SearchModal = ({ isOpen, onClose, data, onSelect, currentSelection = [] }) => {
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Nowy stan na dane ładowane dynamicznie
  const [searchSource, setSearchSource] = useState([]);
   
  const [toast, setToast] = useState(null); 
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  // --- LOGIKA ŁADOWANIA DANYCH (ZABEZPIECZENIE) ---
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSearchText("");
      setFilteredData([]);
      setSelectedIndex(0);
      setToast(null);
      document.body.style.overflow = "hidden";

      // Strategia ładowania danych: Props -> LocalStorage -> API
      const loadData = async () => {
        // 1. Jeśli przekazano dane w propsach, użyj ich
        if (data && data.length > 0) {
            setSearchSource(data);
            return;
        }

        // 2. Jeśli nie, spróbuj LocalStorage
        try {
            const storedData = localStorage.getItem('training_search_data');
            if (storedData) {
                const parsed = JSON.parse(storedData);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setSearchSource(parsed);
                    return;
                }
            }
        } catch (e) {
            console.warn("Błąd odczytu LS w modalu");
        }

        // 3. Ostateczność: Pobierz bezpośrednio z API
        try {
            console.log("SearchModal: Pobieranie awaryjne z API...");
            const res = await api.get('/training-search-data/');
            if (res.data && res.data.length > 0) {
                setSearchSource(res.data);
                // Naprawiamy cache przy okazji
                localStorage.setItem('training_search_data', JSON.stringify(res.data));
            }
        } catch (err) {
            console.error("SearchModal: Błąd API:", err);
        }
      };

      loadData();

    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen, data]);

  // Fuse korzysta teraz z searchSource zamiast data z propsów
  const fuse = useMemo(() => {
    return new Fuse(searchSource, {
      keys: [
        { name: "source_label", weight: 0.8 },
        { name: "content_label", weight: 0.4 },
        { name: "search_vector_text", weight: 0.2 }
      ],
      minMatchCharLength: 1,
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true,
      useExtendedSearch: true,
    });
  }, [searchSource]);

  const executeSearch = () => {
    // UWAGA: Wykomentowano blur, bo to on powoduje crash UI na iOS
    // inputRef.current?.blur();

    if (!searchText.trim()) {
      setFilteredData([]);
      return;
    }

    // Jeśli dane się jeszcze nie załadowały, nie szukaj
    if (searchSource.length === 0) return;

    const fuseResults = fuse.search(searchText);
    let processedResults = fuseResults.map(res => ({ ...res.item, score: res.score }));
    const queryNumbers = searchText.match(/\d+/g); 
    
    processedResults.sort((a, b) => {
        const checkExactNumbers = (text) => {
            if (!queryNumbers) return true; 
            return queryNumbers.every(num => new RegExp(`(^|\\D)${num}(\\D|$)`).test(text));
        };
        const aHasExactNums = checkExactNumbers(a.source_label);
        const bHasExactNums = checkExactNumbers(b.source_label);
        if (aHasExactNums && !bHasExactNums) return -1;
        if (!aHasExactNums && bHasExactNums) return 1;

        if (queryNumbers) {
            if (a.type === 'MATURA' && b.type === 'SUBFIELD') return -1;
            if (a.type === 'SUBFIELD' && b.type === 'MATURA') return 1;
        }
        return a.score - b.score;
    });

    setFilteredData(processedResults.slice(0, 50));
    setSelectedIndex(0);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClear = () => {
      setSearchText("");
      setFilteredData([]);
      inputRef.current?.focus();
  };

  const handleItemClick = (item) => {
    const isAlreadySelected = currentSelection.includes(item.target_subfield_id);
    const subfieldName = item.type === 'MATURA' ? item.context_info : item.source_label;
    onSelect(item.target_subfield_id, item.target_field_id, subfieldName);

    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);

    if (isAlreadySelected) {
        setToast({
            type: 'remove',
            title: item.source_label,
        });
    } else {
        let payload = {};
        try {
            if (typeof item.payload === 'string') payload = JSON.parse(item.payload);
            else if (typeof item.payload === 'object' && item.payload !== null) payload = item.payload;
        } catch (e) {}

        const toastTitle = item.type === 'MATURA' ? item.context_info : item.source_label;
        const tasks = payload.preview_list || [];

        setToast({
            type: 'add',
            title: toastTitle,
            items: tasks 
        });
    }

    toastTimeoutRef.current = setTimeout(() => {
        setToast(null);
    }, 3000);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredData.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault(); 
      executeSearch();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const handleFormSubmit = (e) => {
      e.preventDefault();
      executeSearch();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[5vh] lg:pt-[8vh] px-4 animate-in fade-in duration-200">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-all"
        onClick={onClose} 
      />

      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] lg:max-h-[75vh] ring-1 ring-gray-900/5">
        
        {/* HEADER - ORYGINALNY WYGLĄD */}
        <div className="px-4 pt-4 pb-2 bg-white z-20 border-b border-gray-100">
            {/* Tytuł i przycisk zamknięcia */}
            <div className="relative flex items-center justify-center mb-1">
                <h2 className="text-lg font-bold font-apotek uppercase text-[#233855] tracking-wide">
                    Wyszukaj Temat
                </h2>
                <button onClick={onClose} className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 -mr-2 text-gray-400 hover:text-gray-600">
                    <FaTimes />
                </button>
            </div>
            
            {/* Tekst instrukcji */}
            <p className="text-[11px] text-center text-slate-400 leading-snug mb-3 max-w-lg mx-auto">
                {searchSource.length > 0 
                  ? <>Wyszukaj zadanie maturalne lub zagadnienie (np. "logarytmy"), aby dodać <strong className="text-[#233855]">cały temat</strong> do treningu dowolnego.</>
                  : "Ładowanie bazy zadań..."
                }
            </p>

            {/* Formularz */}
            <form onSubmit={handleFormSubmit} className="flex gap-2 items-center">
                 <div className="relative flex-1 bg-white border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#233855]/20 focus-within:border-[#233855] transition-all">
                     <input
                       ref={inputRef}
                       type="text"
                       enterKeyHint="search"
                       className="w-full bg-transparent text-gray-800 text-sm py-2 pl-3 pr-8 focus:outline-none placeholder-gray-400 border-none m-0 rounded-lg"
                       placeholder="Wpisz zadanie lub zagadnienie..."
                       value={searchText}
                       onChange={(e) => setSearchText(e.target.value)}
                       onKeyDown={handleKeyDown}
                     />
                     {searchText && (
                         <button type="button" onClick={handleClear} className="absolute right-1 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600">
                            <FaTimes className="w-3 h-3" />
                         </button>
                     )}
                 </div>

                 {/* Przycisk Szukaj */}
                 <button 
                    type="submit"
                    className="bg-[#233855] hover:bg-[#345081] text-white px-2 py-2 rounded-lg shadow-sm transition-colors flex-shrink-0 flex items-center justify-center"
                    title="Szukaj"
                 >
                    <FaSearch className="w-4 h-4" />
                 </button>
            </form>
        </div>

        {/* LISTA WYNIKÓW */}
        <div ref={listRef} className="overflow-y-auto bg-gray-50/30 p-4 min-h-[100px] relative pb-20 flex-1 space-y-3">
          {filteredData.length === 0 && searchText.length > 0 && (
            <div className="py-12 text-center">
              <p className="text-gray-400 text-xs mb-2">Brak wyników lub nie kliknięto szukaj.</p>
              <button onClick={executeSearch} className="text-[#233855] font-bold text-sm underline">Szukaj</button>
            </div>
          )}

          {filteredData.map((item, idx) => {
            const isSelected = idx === selectedIndex;
            const isMaturaMatch = item.type === 'MATURA';
            const isAdded = currentSelection.includes(item.target_subfield_id);

            const mainTitle = isMaturaMatch ? item.context_info : item.source_label;
            
            let payload = {};
            try {
                if (typeof item.payload === 'string') payload = JSON.parse(item.payload);
                else if (typeof item.payload === 'object' && item.payload !== null) payload = item.payload;
            } catch (e) {}

            return (
              <button
                type="button" 
                key={item.id || idx}
                onClick={() => handleItemClick(item)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`w-full text-left p-3 rounded-lg border transition-all duration-200 group flex items-start relative
                  ${isSelected 
                      ? "bg-white border-[#233855] shadow-md ring-1 ring-[#233855]/10" 
                      : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"}
                  ${isAdded ? "!bg-[#007D00]/5 !border-[#007D00]/30" : ""}
                `}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                      <div className="font-bold text-sm mb-1 text-gray-800 line-clamp-2">
                        {mainTitle}
                      </div>
                      
                      {!isAdded && isSelected && (
                        <div className="text-lg ml-2 text-[#233855]">
                            <FaPlusCircle />
                        </div>
                      )}
                  </div>

                  {isMaturaMatch ? (
                      <div className="mt-1">
                          <div className={`flex flex-col p-2 rounded border text-xs ${isAdded ? 'bg-[#007D00]/5 border-[#007D00]/20' : 'bg-gray-50 border-gray-100'}`}>
                              <div className="font-bold text-gray-700 mb-0.5 truncate">
                                  Temat zawiera: {item.source_label}
                              </div>
                              <div className="text-gray-500 truncate-latex line-clamp-1 max-h-[1.4em]">
                                  <RenderMarkdownWithLatex text={item.content_label} />
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="mt-1">
                          <div className="text-xs text-gray-500 mb-2">
                              {item.content_label}
                          </div>
                          
                          {payload.badges && payload.badges.length > 0 && (
                             <div className="flex flex-wrap gap-1.5 opacity-90 mb-2">
                                {payload.badges.slice(0, 3).map((b, i) => (
                                   <span key={i} className="inline-flex items-center text-[10px] px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-gray-600 whitespace-nowrap">
                                      <FaGraduationCap className="mr-1.5 w-3 h-3 text-[#233855]" />
                                      {b.replace("Zadanie ", "Zad. ")}
                                   </span>
                                ))}
                             </div>
                          )}

                          {payload.preview_list && payload.preview_list.length > 0 && (
                             <div className={`mt-2 p-2 rounded border text-xs ${isSelected ? 'bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-100'}`}>
                                <div className="font-bold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5 text-[9px]">
                                   <FaLayerGroup className="w-2.5 h-2.5" />
                                   Przykłady:
                                </div>
                                <ul className="space-y-1.5">
                                   {payload.preview_list.slice(0, 3).map((question, qIdx) => (
                                      <li key={qIdx} className="flex items-start gap-2 text-gray-600">
                                         <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                                         <div className="truncate-latex line-clamp-1 max-h-[1.4em] flex-1 min-w-0">
                                            <RenderMarkdownWithLatex text={question} />
                                         </div>
                                      </li>
                                   ))}
                                </ul>
                             </div>
                          )}
                      </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* TOAST */}
        {toast && (
            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-50 animate-bounce-in w-[90%] max-w-md pointer-events-none">
                {toast.type === 'add' ? (
                    <div className="bg-[#233855] text-white p-4 rounded-xl shadow-2xl border border-slate-600 flex flex-col gap-2">
                        <div className="flex items-center gap-2 border-b border-slate-500 pb-2 mb-1">
                            <FaCheckCircle className="text-green-400 w-4 h-4" />
                            <span className="text-sm font-bold text-white uppercase tracking-wider font-apotek">
                                Dodano temat do treningu
                            </span>
                        </div>
                        <div className="font-bold text-sm text-white pl-1">{toast.title}</div>
                        {toast.items && toast.items.length > 0 && (
                            <div className="bg-slate-700/50 rounded p-2 mt-1">
                                <ul className="space-y-1.5">
                                    {toast.items.slice(0, 3).map((q, i) => (
                                        <li key={i} className="text-[11px] text-slate-200 flex items-start gap-2 opacity-90">
                                            <span className="mt-1.5 w-1 h-1 bg-green-400 rounded-full flex-shrink-0" />
                                            <div className="line-clamp-1 truncate-latex"><RenderMarkdownWithLatex text={q} /></div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-slate-800 text-white p-4 rounded-xl shadow-2xl border border-slate-600 flex items-center gap-3">
                        <div className="bg-red-500/20 p-2 rounded-full text-red-400">
                            <FaTimes className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-red-400 uppercase tracking-wider font-apotek mb-0.5">
                                Usunięto z treningu
                            </div>
                            <div className="font-bold text-sm text-white line-clamp-1">
                                {toast.title}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )}

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 px-5 py-3 flex justify-between items-center absolute bottom-0 left-0 right-0 z-20">
           <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
              {filteredData.length} wyników
           </span>
           <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold uppercase tracking-wider px-5 py-2 rounded-lg transition-colors">
              Zamknij
           </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;